import getSymbolFromCurrency from "currency-symbol-map";
import type mongoose from "mongoose";

import {UTC_TIMEZONE, yearInDays} from "../../constant";
import type {Listing, ListingDocument, ListingImage, LockDocument} from "../../generated/models";
import {removeExtraSpace} from "../../utils/removeExtraSpace";

import getChannelLabel from "./getChannelLabel";

export function getListingName<T extends {airbnbName?: string; nickname?: string; name?: string}>(
    listing: T
) {
    return listing.nickname || listing.airbnbName || listing.name || "";
}

export function getListingThumbnail<
    T extends {airbnbThumbnailUrl?: string; images?: ListingImage[]}
>(listing: T): string | undefined {
    return listing.images?.[0]?.url || listing.airbnbThumbnailUrl;
}

export function getLabelPriceSource(priceSource: ListingDocument["priceSource"]) {
    if (!priceSource) {
        return "Amount";
    }
    if (priceSource === "Amount" || priceSource === "PriceLabs") {
        return priceSource;
    }
    return getChannelLabel(priceSource);
}

export const getListingTimezone = <T extends {timeZone?: string}>(listing: T) =>
    listing.timeZone || UTC_TIMEZONE;

type BaseListingIDs = {listingIDs?: LockDocument["listingIDs"] | string[]};

export const isListingPricingPro = <T extends {pricingPlan?: string}>(listing: T) => {
    return listing.pricingPlan === "Pro";
};

export const isProPlan = <
    T extends {
        _id: mongoose.Types.ObjectId | string;
        pricingEnabled?: boolean;
        syncAvailability?: boolean;
        pricingPlan?: Listing["pricingPlan"];
    },
    L extends BaseListingIDs,
    G extends Required<BaseListingIDs>
>(
    listing: T,
    locks: L[],
    listingGroups: G[]
) => {
    const isPricing = !!listing.pricingEnabled;
    const isAvailability = !!listing.syncAvailability;
    const hasLock = locks.some(
        lock =>
            lock.listingIDs?.length &&
            lock.listingIDs.some(listingID => listing._id.toString() === listingID.toString())
    );
    const isTurnover = listingGroups.some(listingGroup =>
        listingGroup.listingIDs.some(
            listingID => listingID && listing._id.toString() === listingID.toString()
        )
    );
    const isPro = isListingPricingPro(listing);
    return {isPro, isPricing, isAvailability, hasLock, isTurnover};
};

export const getCalendarExportCode = <T extends {calendarExportCode?: string}>(
    listing: T,
    isBeta: boolean
) => {
    if (!listing.calendarExportCode) {
        return "";
    }
    // eslint-disable-next-line no-undef
    if (isBeta) {
        return `https://beta.hosttools.com/ical/${listing.calendarExportCode}`;
    }

    return `https://app.hosttools.com/ical/${listing.calendarExportCode}`;
};

export function getCurrencySymbol(listingCurrency = "USD") {
    return getSymbolFromCurrency(listingCurrency) || "$";
}

export function getBookingWindowText(listing: {bookingWindow: ListingDocument["bookingWindow"]}) {
    if (listing.bookingWindow === 0) {
        return "2 years";
    }
    if (listing.bookingWindow === yearInDays) {
        return "1 year";
    }
    if (listing.bookingWindow === 547) {
        return "1.5 years";
    }
    return `${listing.bookingWindow} days`;
}

export function buildListingAddress<
    T extends {
        streetNumber?: string;
        streetName?: string;
        apartment?: string;
        city?: string;
        region?: string;
        zipcode?: string;
        country?: string;
    }
>(listing: T) {
    return removeExtraSpace(
        `${listing.streetNumber ?? ""} ${listing.streetName ?? ""} ${listing.apartment ?? ""} ${
            listing.city ?? ""
        } ${listing.region ?? ""} ${listing.zipcode ?? ""} ${listing.country ?? ""}`
    );
}

export function sortListingsAlphabetically<
    T extends {airbnbName?: string; nickname?: string; name?: string}
>(listings: T[]): T[] {
    return [...listings].sort((a, b) => {
        const nameA = getListingName(a).toLowerCase();
        const nameB = getListingName(b).toLowerCase();
        return nameA.localeCompare(nameB);
    });
}
